import { Controller } from "@hotwired/stimulus"

function hideElement(element) {
  if (!element) return

  if (element.classList) element.classList.add("tw-hidden")
  if (typeof element.setAttribute === "function") element.setAttribute("aria-hidden", "true")
  if (element.style) element.style.display = "none"
}

function dispatchStackUpdated(stack) {
  if (!stack) return
  stack.dispatchEvent(new CustomEvent("flash-stack:updated"))
}

export function addFlashBanner({ type = "notice", html, material = true } = {}) {
  if (!html || typeof document === "undefined") return

  const stack = document.querySelector("[data-controller~='flash-stack'], [data-flash-stack='true']")
  if (!stack) {
    console.warn("Flash stack not found")
    return
  }

  const block = stack.querySelector("[data-flash-stack-block]")
  const template = stack.querySelector("template[data-flash-banner-template]")
  if (!block || !template) {
    console.warn("Flash stack template missing")
    return
  }

  const normalized = (type || "notice").toLowerCase()
  const buttonClass = material ? "md-primary-btn md-blue-btn" : "link_button"
  const rendered = document.createElement("template")
  rendered.innerHTML = Mustache.to_html(template.innerHTML, {
    type: normalized,
    title: normalized,
    html,
    button_class: buttonClass
  }).trim()

  block.prepend(rendered.content.firstElementChild)
  dispatchStackUpdated(stack)
}

global.addFlashBanner = addFlashBanner

function dismissBanner(link) {
  if (!link || typeof link.closest !== "function") return false

  const banner = link.closest("[data-flash-banner]")

  if (banner) {
    const stack = banner.closest("[data-controller~='flash-stack'], [data-flash-stack]")

    if (stack) {
      banner.remove()
      dispatchStackUpdated(stack)
    } else {
      hideElement(banner)
    }
  } else if (link.parentElement) {
    hideElement(link.parentElement)
  }

  if (typeof document !== "undefined" && document.getElementById("search_results")) {
    document.cookie = "search_notice_closed=true; path=/"
  }

  return false
}

export default class extends Controller {
  static targets = ["count", "banner", "toggle"]
  static values  = { expanded: { type: Boolean, default: false } }

  connect() {
    this._toggle = this.hasToggleTarget ? this.toggleTarget : this._toggle || null
    this.syncStickyOffset = this.syncStickyOffset.bind(this)

    const spacer = this.element.nextElementSibling
    this._spacer = spacer?.classList.contains("flash-stack__spacer") ? spacer : null

    if (this._toggle && !this._togglePlaceholder) {
      this._togglePlaceholder = document.createComment("flash-stack-toggle-home")
      this._toggle.parentNode?.insertBefore(this._togglePlaceholder, this._toggle)
    }

    this.updateHandler = () => this.refresh()
    this.element.addEventListener("flash-stack:updated", this.updateHandler)
    window.addEventListener("resize", this.syncStickyOffset)

    this.observeStickyBanners()
    this.observeBannerMutations()
    this.syncStickyOffset()
    this.refresh()
  }

  disconnect() {
    this.element.removeEventListener("flash-stack:updated", this.updateHandler)
    window.removeEventListener("resize", this.syncStickyOffset)
    this.headerWrapper?.style.removeProperty("top")
    document.body.style.removeProperty("--app-topbar-height")
    document.body.style.removeProperty("--sticky-banner-offset")
    this._spacer?.style.removeProperty("height")
    this._spacer = null
    this._bannerObserver?.disconnect()
    this._bannerObserver = null
    this._stickyBannerObserver?.disconnect()
    this._stickyBannerObserver = null
  }

  // --- actions ---------------------------------------------------------------
  toggle(event) {
    event.preventDefault()
    this.expandedValue = !this.expandedValue
  }

  dismiss(event) {
    event.preventDefault()
    dismissBanner(event.currentTarget)
  }

  expandedValueChanged(value) {
    this.element.dataset.expanded = value
    this._toggle?.setAttribute("aria-expanded", String(value))
    this.updateVisibility()
  }

  // --- main flow -------------------------------------------------------------
  refresh() {
    this.ensureToggleCache()
    this.normalizeBanners()
    this.updateCount()
    if (this.collapseIfNecessary()) return
    this.positionToggle()
    this.updateVisibility()
  }

  normalizeBanners() {
    this.bannerTargets.forEach(b => b.setAttribute("data-flash-stack-target", "banner"))
  }

  // If top banner changes, move the toggle to the new primary
  positionToggle() {
    const primary = this.bannerTargets[0]
    if (!this._toggle) return

    if (this.bannerTargets.length > 1 && primary) {
      if (this._toggle.parentNode !== primary) {
        primary.insertBefore(this._toggle, primary.firstChild)
      }
    } else {
      this.restoreToggle()
    }
  }

  restoreToggle() {
    if (this._togglePlaceholder?.parentNode && this._toggle) {
      this._togglePlaceholder.parentNode.insertBefore(this._toggle, this._togglePlaceholder)
    }
  }

  updateVisibility() {
    const collapsed = !this.expandedValue

    this.bannerTargets.forEach((banner, idx) => {
      const hide = collapsed && idx > 0
      banner.classList.toggle("tw-hidden", hide)
    })

    if (this._toggle) {
      const hideToggle = this.bannerTargets.length <= 1
      this._toggle.classList.toggle("tw-hidden", hideToggle)
      if (hideToggle) this._toggle.setAttribute("disabled", "disabled")
      else this._toggle.removeAttribute("disabled")
    }

    this.syncHeaderOffset()
  }

  updateCount() {
    if (this.hasCountTarget) this.countTarget.textContent = String(this.bannerTargets.length)
  }

  // Hide whole stack when empty; tidy when single
  collapseIfNecessary() {
    const n = this.bannerTargets.length

    if (n === 0) {
      this.restoreToggle()
      this.element.classList.add("tw-hidden")
      this.element.setAttribute("aria-hidden", "true")
      this.syncHeaderOffset()
      return true
    }

    this.element.classList.remove("tw-hidden")
    this.element.removeAttribute("aria-hidden")

    if (n === 1) {
      const single = this.bannerTargets[0]
      if (this._toggle && this._toggle.parentNode === single) this.restoreToggle()
      single.setAttribute("data-flash-stack-target", "banner")
    }

    return false
  }

  ensureToggleCache() {
    if (!this._toggle && this.hasToggleTarget) this._toggle = this.toggleTarget
  }

  syncStickyOffset() {
    const offset = Array.from(document.querySelectorAll("[data-sticky-banner]"))
      .filter((banner) => !banner.hidden && window.getComputedStyle(banner).display !== "none")
      .reduce((height, banner) => height + banner.getBoundingClientRect().height, 0)

    this.element.style.top = `${offset}px`
    this.syncHeaderOffset()
  }

  syncHeaderOffset() {
    const stickyTop = Number.parseFloat(this.element.style.top) || 0
    const stackHeight = this.element.classList.contains("tw-hidden")
      ? 0
      : this.element.getBoundingClientRect().height

    if (this._spacer) this._spacer.style.height = `${stackHeight}px`

    const headerTop = stickyTop + stackHeight
    if (this.headerWrapper) this.headerWrapper.style.top = `${headerTop}px`

    this.publishTopbarHeight(headerTop)
  }

  publishTopbarHeight(headerTop) {
    const headerHeight = this.headerWrapper?.getBoundingClientRect().height || 0
    document.body.style.setProperty("--sticky-banner-offset", `${headerTop}px`)
    document.body.style.setProperty("--app-topbar-height", `${headerTop + headerHeight}px`)
  }

  get headerWrapper() {
    return document.querySelector(".application-menu-header .header-wrapper")
  }

  observeStickyBanners() {
    if (this._stickyBannerObserver) return

    this._stickyBannerObserver = new MutationObserver(this.syncStickyOffset)
    this._stickyBannerObserver.observe(document.body, { childList: true, subtree: true })
  }

  observeBannerMutations() {
    if (this._bannerObserver || typeof MutationObserver === "undefined") return

    this._bannerObserver = new MutationObserver(muts => {
      const bannerTouched = muts.some(m => this.mutationTouchesBanner(m))
      const toggleDetached = this._toggle && !this.element.contains(this._toggle)
      if (bannerTouched || toggleDetached) this.refresh()
    })

    this._bannerObserver.observe(this.element, { childList: true, subtree: true })
  }

  mutationTouchesBanner(mutation) {
    const nodes = [...mutation.addedNodes, ...mutation.removedNodes]
    return nodes.some(n => this.isBannerNode(n))
  }

  isBannerNode(node) {
    return (
      node &&
      node.nodeType === Node.ELEMENT_NODE &&
      typeof node.matches === "function" &&
      node.matches("[data-flash-banner]")
    )
  }
}
